import { useCallback, useEffect, useState } from "react";
import {
  createWorkplaceChatDm,
  getWorkplaceChatStatus,
  getWorkplaceChatToken,
  patchWorkplaceChatBridgeMute,
  syncWorkplaceChatBridge,
  type WorkplaceChatTokenPayload,
} from "@/lib/workplaceChatClient";

export type UseWorkplaceChatVendorStateResult = {
  vendorEnabled: boolean | null;
  vendorReady: boolean;
  useFallback: boolean;
  fallbackReason: string | null;
  bridgeEnabled: boolean;
  bridgeChannelSlugs: string[];
  mutedChannelIds: string[];
  loading: boolean;
  errorText: string;
  tokenPayload: WorkplaceChatTokenPayload | null;
  startDm: (recipientUserIds: string[]) => Promise<{ ok: boolean; error?: string }>;
  setBridgeMuted: (channelSlug: string, muted: boolean) => Promise<void>;
  reload: () => void;
};

export function useWorkplaceChatVendorState(active: boolean): UseWorkplaceChatVendorStateResult {
  const [vendorEnabled, setVendorEnabled] = useState<boolean | null>(null);
  const [vendorReady, setVendorReady] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [bridgeEnabled, setBridgeEnabled] = useState(false);
  const [bridgeChannelSlugs, setBridgeChannelSlugs] = useState<string[]>([]);
  const [mutedChannelIds, setMutedChannelIds] = useState<string[]>([]);
  const [tokenPayload, setTokenPayload] = useState<WorkplaceChatTokenPayload | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorText("");
      const statusRes = await getWorkplaceChatStatus();
      if (cancelled) return;
      if (!statusRes.ok || !statusRes.data?.enabled) {
        setVendorEnabled(false);
        setVendorReady(false);
        setUseFallback(false);
        setLoading(false);
        return;
      }
      setVendorEnabled(true);
      setBridgeEnabled(statusRes.data.bridge_enabled === true);
      setBridgeChannelSlugs(statusRes.data.bridge_channel_slugs ?? []);
      setMutedChannelIds(statusRes.data.bridge_muted_channel_ids ?? []);
      if (statusRes.data.bridge_enabled) {
        void syncWorkplaceChatBridge();
      }
      if (statusRes.data.fallback_reason) {
        setUseFallback(true);
        setFallbackReason(statusRes.data.fallback_reason);
        setVendorReady(false);
        setLoading(false);
        return;
      }
      const tokenRes = await getWorkplaceChatToken();
      if (cancelled) return;
      if (!tokenRes.ok) {
        setUseFallback(true);
        setFallbackReason("token_failed");
        setVendorReady(false);
        setErrorText(tokenRes.error || "Unable to connect to team chat.");
        setLoading(false);
        return;
      }
      if (!tokenRes.data?.token) {
        setUseFallback(true);
        setFallbackReason("token_failed");
        setVendorReady(false);
        setErrorText("Unable to connect to team chat.");
        setLoading(false);
        return;
      }
      setTokenPayload(tokenRes.data);
      setUseFallback(false);
      setFallbackReason(null);
      setVendorReady(true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [active, reloadKey]);

  const startDm = useCallback(async (recipientUserIds: string[]) => {
    const r = await createWorkplaceChatDm(recipientUserIds);
    if (!r.ok) return { ok: false, error: r.error || "Policy blocked or server error" };
    return { ok: true };
  }, []);

  const setBridgeMuted = useCallback(async (channelSlug: string, muted: boolean) => {
    const r = await patchWorkplaceChatBridgeMute({ channel_slug: channelSlug, muted });
    if (r.ok && r.data) setMutedChannelIds(r.data.bridge_muted_channel_ids ?? []);
  }, []);

  return {
    vendorEnabled,
    vendorReady,
    useFallback,
    fallbackReason,
    bridgeEnabled,
    bridgeChannelSlugs,
    mutedChannelIds,
    loading,
    errorText,
    tokenPayload,
    startDm,
    setBridgeMuted,
    reload,
  };
}
